/**
 * InlineEditableFieldRow — makes a single overview field row click-to-edit.
 *
 * Wraps the canonical {@link OverviewFieldRow} layout (icon → label → value
 * slot) without reimplementing its geometry. In edit state it renders the
 * canonical {@link CrmFieldValueInput} through `useInlineEdit`, the shared
 * headless lifecycle also used by the table and pipeline-card editors, so
 * commit/cancel/error semantics stay identical across every inline-edit
 * surface.
 *
 * The read-only value (`children`) is supplied by the caller — this
 * component never re-renders a field's value itself, it only decides HOW
 * the value region starts an edit.
 *
 * The CELL is the affordance. In read state the whole value column is one
 * rounded cell that lights up on row hover: the value, then any
 * caller-supplied trailing control (copy) directly beside it, then the
 * pencil and clear (✕) pinned to the cell's right edge — so the row's
 * actions are always in the same place and the target for "edit this" is
 * the full width of the column, not the width of the text. The cell has
 * exactly the footprint of the editor that replaces it (a 32px `xs`
 * control), so hover → edit is a colour change and nothing below the row
 * moves.
 *
 * Any click on the cell that is not on a control starts editing. If the
 * rendered value owns its own interactive element (an association badge, a
 * domain `<a href>`, an `ExpandablePropertyValue` "Show more" toggle —
 * detected at runtime, since the toggle only exists when content
 * overflows), a click ON that element keeps its own behaviour (navigate,
 * mailto, expand) and a DOUBLE click on it starts editing. The pencil is
 * the row's one keyboard control for editing.
 *
 * `isEditable` defaults to `false` (fail-closed): no cell, no pencil, no
 * click target, no cursor change.
 */

import { Edit02, XClose } from '@untitledui/icons';
import {
  type FC,
  type MouseEvent,
  type ReactNode,
  type RefObject,
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';

import { CellFieldOverlay } from '@/lib/base/table/components/cell-field-overlay';
import { CrmFieldValueInput } from '@/lib/crm/components/fields/CrmFieldValueInput';
import type { StageAssociationValue } from '@/lib/crm/components/properties/inputs/AssociatedPipelineStageInput';
import type { AssociationType } from '@/lib/crm/core/SchemaClient';
import type { PropertyTypeSchema } from '@/lib/crm/hooks/usePropertySchema';
import {
  classifyEditorKind,
  type EditorKindDescriptor,
} from '@/lib/crm/inline-edit/editorKinds';
import { useInlineEdit } from '@/lib/crm/inline-edit/useInlineEdit';
import { usePickerOpenGuard } from '@/lib/crm/inline-edit/usePickerOpenGuard';
import { humanizeSaveError } from '@/lib/crm/utils/humanizeSaveError';
import { cx } from '@/utils/cx';

import { InlineFieldError } from './InlineFieldError';
import { OverviewFieldRow } from './OverviewFieldRow';
import { RowActionButton } from './RowActionButton';

interface InlineEditableFieldRowCommonProps {
  icon: FC<{ className?: string }>;
  label: string;
  /** Current persisted value. */
  value: unknown;
  /** Async save; throwing shows the humanized error and keeps the row open. */
  onSave: (value: unknown) => Promise<void>;
  /**
   * Clears the field — saves its empty value — without opening the editor.
   * Supply it only when the field currently holds a value the user may
   * remove; the row then shows a ✕ beside the pencil. Throwing shows the
   * humanized error under the value and leaves the value in place.
   */
  onClear?: () => Promise<void>;
  /** Fail-closed: no pencil, no click target, no cursor change until set. */
  isEditable?: boolean;
  rowClassName?: string;
  valueClassName?: string;
  /**
   * Control rendered directly after the value (e.g. a copy button) in READ
   * state only — hidden while the editor is open, which fills the value
   * slot.
   */
  trailing?: ReactNode;
  /** Read-only rendering of `value` — the caller's canonical label component. */
  children: ReactNode;
}

export type InlineEditableFieldRowProps = InlineEditableFieldRowCommonProps &
  (
    | {
        kind: 'property';
        schema: PropertyTypeSchema;
        orgId?: number;
        entityProperties?: Record<string, unknown>;
      }
    | {
        kind: 'stage';
        entityTypeName: string;
        associationTypeName: string;
      }
    | {
        kind: 'association';
        associationType: AssociationType;
        orgId: number;
        stageTypeNames?: Set<string>;
        sourceEntityId?: string;
        /**
         * Restricts the picker to one target entity type — set when this row
         * renders a single per-type subset of a kind-based rule, so a "Deals"
         * row offers deals rather than every case-kind record.
         */
        targetEntityTypeName?: string;
      }
  );

// Matches the ways a read-only value can own its own click: a link (domain
// values), a button (the "Show more" toggle), or an ARIA button/link —
// EntityBadge renders `<span role='button'>`, not a real `<button>`, so the
// role selectors are load-bearing for association rows.
const OWN_INTERACTIVE_SELECTOR =
  'a[href], button, [role="button"], [role="link"]';

// The read-state cell. Lights up on ROW hover (the row carries `group`), so
// hovering the label reveals the affordance too; the actions cluster sits
// at its right edge inside `pr-1`. The whole cell is a click target.
const CELL_CLASS =
  'flex min-h-8 min-w-0 cursor-pointer items-center gap-1 rounded-md pr-1 transition-colors group-hover:bg-primary_hover group-focus-within:bg-primary_hover';

// Cell and edit-wrapper geometry cancel the editor's chrome exactly so
// hover → edit shifts NOTHING: every inline editor renders at `xs`
// (`py-1.5 px-2.5` on the shared control scale, a 32px box), and the cell
// pads itself out to that same box. Property rows are a 20px text line
// (`-my-1.5` → 32px); badge rows are 22px (`min-h-[22px]` on the value
// slot, `-my-[5px]` → 32px). Only the LEFT edge is pulled out: the value
// slot already spans to the panel edge, so a matching right pull would push
// the cell past it. The cell's inner padding restores the value to the spot
// it occupies on a non-editable row.
// The edit wrapper is `flex-1 min-w-0` because a badge row's value slot is a
// flex-wrap container: without it the wrapper — and so the picker trigger —
// shrinks to its content ("Select owner" ends where its label ends) instead
// of spanning the column like every other editor. Inert in a block slot.
const PROPERTY_CELL_CLASS = '-my-1.5 -ml-2.5 py-1.5 pl-2.5';
const PROPERTY_EDIT_WRAPPER_CLASS = '-my-1.5 -ml-2.5 min-w-0 flex-1';
const PICKER_CELL_CLASS = '-my-[5px] -ml-2.5 py-[5px] pl-2.5';
const PICKER_EDIT_WRAPPER_CLASS = '-my-[5px] -ml-2.5 min-w-0 flex-1';

const ACTIONS_CLASS = 'flex shrink-0 items-center gap-1';

// Where focus goes when an editor opens: its text input if it has one (a
// multi-value editor's tags — each with a remove button — come BEFORE the
// input in DOM order and re-render as their objects resolve, so "the first
// focusable" landed on a tag that was then replaced under the cursor),
// otherwise a picker's trigger button.
const EDITOR_INPUT_SELECTOR = 'input:not([type="hidden"]), textarea';
const EDITOR_FOCUSABLE_SELECTOR =
  'input, textarea, select, button, [tabindex]:not([tabindex="-1"])';

function focusEditor(container: HTMLElement): void {
  const target =
    container.querySelector<HTMLElement>(EDITOR_INPUT_SELECTOR) ??
    container.querySelector<HTMLElement>(EDITOR_FOCUSABLE_SELECTOR);
  target?.focus({ preventScroll: true });
}

/**
 * How long a lone click on a value that owns its own click (badge, link)
 * waits for a second click before it is replayed as the value's own action.
 * Matches the OS double-click window closely enough that a deliberate
 * double click never leaks a navigation, while a single click still feels
 * immediate.
 */
export const OWN_VALUE_DOUBLE_CLICK_MS = 250;

/**
 * Whether `ref`'s subtree currently contains its own interactive element.
 * Re-checked on mount and on any DOM mutation inside the subtree — the
 * ExpandablePropertyValue "Show more" toggle mounts asynchronously (only
 * once CollapsibleContent measures an overflow), so a single mount-time
 * check would miss it.
 *
 * While disabled (the row is editing, so the read value is unmounted) the
 * last answer is KEPT, not reset: the first read render after a commit
 * then lands straight in the right branch. Resetting to false made that
 * render mount the plain `role="button"` region, hand it focus, and
 * immediately swap it for the badge + pencil once this re-checked — which
 * unmounted the focused element and dropped focus to <body>. The re-check
 * on re-enable still runs synchronously (layout effect), so a value whose
 * children changed during the edit is corrected before paint.
 */
function useHasOwnInteractiveValue(
  ref: RefObject<HTMLElement | null>,
  enabled: boolean,
  resetKey: unknown
): boolean {
  const [hasOwnInteractive, setHasOwnInteractive] = useState(false);

  useLayoutEffect(() => {
    if (!enabled) {
      return undefined;
    }
    const el = ref.current;
    if (!el) {
      return undefined;
    }
    const check = () => {
      setHasOwnInteractive(el.querySelector(OWN_INTERACTIVE_SELECTOR) != null);
    };
    check();
    const observer = new MutationObserver(check);
    observer.observe(el, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['aria-expanded'],
    });
    return () => observer.disconnect();
  }, [ref, enabled, resetKey]);

  return hasOwnInteractive;
}

/**
 * Click handling for a value region whose content owns its own click
 * (association badge → open record, mailto / domain link, "Show more"
 * toggle).
 *
 * A click that lands on the region's whitespace — not on the value's own
 * element — is an unambiguous "edit this" and starts editing at once.
 *
 * A click ON the element is held: a double click must start editing
 * WITHOUT first firing the element's own action (the first click of a
 * double click would otherwise push the company slideout or open the mail
 * client before the second click lands). So the click is intercepted in the
 * capture phase and held for {@link OWN_VALUE_DOUBLE_CLICK_MS}: a second
 * click within the window cancels the held one and starts editing;
 * otherwise the held click is replayed on its original target
 * (`element.click()` runs the anchor's navigation and the badge's React
 * handler alike). The replay is let through by the same capture handler via
 * `replayingRef`.
 */
function useOwnValueClick(startEdit: (e: MouseEvent<HTMLDivElement>) => void) {
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const replayingRef = useRef(false);

  useEffect(
    () => () => {
      if (timerRef.current !== null) {
        clearTimeout(timerRef.current);
      }
    },
    []
  );

  return useCallback(
    (e: MouseEvent<HTMLDivElement>) => {
      if (replayingRef.current) {
        return;
      }
      e.preventDefault();
      e.stopPropagation();
      if (timerRef.current !== null) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
      const target = e.target as HTMLElement;
      const ownControl = target.closest(OWN_INTERACTIVE_SELECTOR);
      // Whitespace in the cell: nothing to disambiguate from, edit now.
      if (!ownControl || !e.currentTarget.contains(ownControl)) {
        startEdit(e);
        return;
      }
      // `detail` counts consecutive clicks within the platform's double-click
      // window — the second click of a double click arrives with detail 2.
      if (e.detail >= 2) {
        startEdit(e);
        return;
      }
      timerRef.current = setTimeout(() => {
        timerRef.current = null;
        replayingRef.current = true;
        try {
          target.click();
        } finally {
          replayingRef.current = false;
        }
      }, OWN_VALUE_DOUBLE_CLICK_MS);
    },
    [startEdit]
  );
}

type ClearStatus = 'idle' | 'clearing' | 'error';

export function InlineEditableFieldRow(props: InlineEditableFieldRowProps) {
  const {
    icon,
    label,
    value,
    onSave,
    onClear,
    isEditable = false,
    rowClassName,
    valueClassName,
    trailing,
    children,
  } = props;

  const descriptor: EditorKindDescriptor =
    props.kind === 'property'
      ? classifyEditorKind({ propertyType: props.schema })
      : classifyEditorKind({
          association: {
            associationType: {
              // Stage associations are always single-target; regular
              // associations carry their own cardinality.
              cardinality:
                props.kind === 'stage'
                  ? 'many_to_one'
                  : props.associationType.cardinality,
            },
          },
        });

  const containerRef = useRef<HTMLDivElement>(null);
  const valueRef = useRef<HTMLDivElement>(null);
  // Tall editors (rich text) float over the page instead of opening in the
  // row: a markdown value reads clamped ("Show more") at ~3 lines, and an
  // in-flow editor showing the full value plus its toolbar shoved every row
  // below it down by hundreds of pixels on click-in. The read cell stays
  // mounted as the anchor (so the page keeps its exact layout) and the
  // editor pops over it in the same CellFieldOverlay the table uses — the
  // Attio "popped field". Held in state, not a ref: the overlay is rendered
  // from the element, so it must be a render input.
  const floatsEditor = descriptor.kind === 'markdown';
  const [cellEl, setCellEl] = useState<HTMLDivElement | null>(null);
  const setCellRef = useCallback((el: HTMLDivElement | null) => {
    setCellEl(el);
  }, []);
  // The pencil: the row's keyboard control, and what useInlineEdit refocuses
  // when the editor closes.
  const triggerRef = useRef<HTMLElement | null>(null);
  const setTriggerRef = useCallback((el: HTMLElement | null) => {
    triggerRef.current = el;
  }, []);

  const {
    isEditing,
    status,
    draft,
    setDraft,
    errorMessage,
    announcement,
    startEdit,
    commit,
    handleKeyDown,
    handleBlur,
  } = useInlineEdit({
    descriptor,
    initialValue: value,
    onSave,
    containerRef,
    triggerRef,
    // The floating (portaled) editor leaves the slideout's DOM; the read
    // cell's value node stays in it, so clicks elsewhere in the panel still
    // commit. Null while an in-row editor is open — the hook then falls back
    // to containerRef, the previous behaviour.
    surfaceRef: valueRef,
  });

  // Clear (✕) is a save of the empty value that never opens the editor, so
  // it has its own tiny lifecycle beside useInlineEdit's: busy while the
  // save runs, an error pill under the value if it fails, and focus handed
  // to the row's trigger once the ✕ itself has gone (an empty field has
  // nothing to clear, so the button unmounts on success).
  const [clearStatus, setClearStatus] = useState<ClearStatus>('idle');
  const [clearError, setClearError] = useState<string | null>(null);
  const [clearAnnouncement, setClearAnnouncement] = useState('');
  const pendingClearRefocusRef = useRef(false);

  // Where the entry click landed, for editors that can drop the caret at the
  // clicked character (markdown's read-size inline editor). Null for entries
  // with no meaningful text point: the pencil button, keyboard, and the
  // own-interactive double-click path.
  const [editCaretPoint, setEditCaretPoint] = useState<{
    x: number;
    y: number;
  } | null>(null);

  const handleStartEdit = useCallback(() => {
    setClearError(null);
    setClearAnnouncement('');
    setEditCaretPoint(null);
    startEdit();
  }, [startEdit]);

  // Cell-click entry: same as handleStartEdit but keeps the click coords so
  // the editor can place the caret under the pointer.
  const handleStartEditAtPoint = useCallback(
    (point: { x: number; y: number }) => {
      setClearError(null);
      setClearAnnouncement('');
      setEditCaretPoint(point);
      startEdit();
    },
    [startEdit]
  );

  const handleClear = useCallback(async () => {
    if (!onClear) {
      return;
    }
    setClearError(null);
    setClearStatus('clearing');
    try {
      await onClear();
      setClearAnnouncement('Cleared');
      pendingClearRefocusRef.current = true;
      setClearStatus('idle');
    } catch (err) {
      setClearError(
        err instanceof Error
          ? humanizeSaveError(err.message)
          : "We couldn't clear this. Try again."
      );
      setClearStatus('error');
    }
  }, [onClear]);

  // Same shape as useInlineEdit's refocus: the trigger is a callback ref on
  // the read-mode element, re-attached on the render that reflects the
  // cleared value, so the focus move has to wait for that commit.
  useLayoutEffect(() => {
    if (clearStatus !== 'idle' || !pendingClearRefocusRef.current) {
      return;
    }
    pendingClearRefocusRef.current = false;
    triggerRef.current?.focus({ preventScroll: true });
  }, [clearStatus]);

  const hasOwnInteractive = useHasOwnInteractiveValue(
    valueRef,
    isEditable && !isEditing,
    children
  );

  // Clicks on a value that owns interactive content (a "Show more" toggle, a
  // link) route through useOwnValueClick; when they do start an edit they
  // carry the click point like a plain cell click, so the caret still lands
  // on the clicked character.
  const handleStartEditFromValueClick = useCallback(
    (e: MouseEvent<HTMLDivElement>) => {
      handleStartEditAtPoint({ x: e.clientX, y: e.clientY });
    },
    [handleStartEditAtPoint]
  );
  const handleOwnValueClickCapture = useOwnValueClick(
    handleStartEditFromValueClick
  );

  usePickerOpenGuard(
    containerRef,
    isEditing,
    status === 'saving',
    focusEditor,
    commit
  );

  const editLabel = `Edit ${label}`;
  const clearLabel = `Clear ${label}`;

  // A click anywhere on the cell that reaches this handler is on whitespace
  // or plain value text: the value's own control (badge, link) is intercepted
  // in the capture phase by useOwnValueClick, and the action buttons stop
  // propagation. Belt and braces for a control that does neither.
  const handleCellClick = useCallback(
    (e: MouseEvent<HTMLDivElement>) => {
      const control = (e.target as HTMLElement).closest(
        OWN_INTERACTIVE_SELECTOR
      );
      if (control && e.currentTarget.contains(control)) {
        return;
      }
      handleStartEditAtPoint({ x: e.clientX, y: e.clientY });
    },
    [handleStartEditAtPoint]
  );

  // Stage editors resolve to a full StageAssociationValue; the row's draft
  // (like every other kind) stays the plain target entity id.
  const handleStageChange = useCallback(
    (assoc: StageAssociationValue | null) => {
      setDraft(assoc?.target_entity_id ?? null);
    },
    [setDraft]
  );

  const disabled = status === 'saving';
  const isPropertyKind = props.kind === 'property';
  // Long-form kinds routinely render multi-line read values, and the cell's
  // items-center would float the copy/pencil/clear controls at the vertical
  // middle of a tall value. Pin them to the FIRST line instead: self-start
  // puts them at the cell's content top (py-1.5 = the first line's top) and
  // mt-[3px] centers the 14px icons within the 20px text-sm line. For a
  // single-line value this lands on the same pixel items-center chose, so
  // short rows are unchanged.
  const alignControlsToFirstLine =
    descriptor.kind === 'markdown' || descriptor.kind === 'textarea';

  let editor: ReactNode;
  if (props.kind === 'property') {
    editor = (
      <CrmFieldValueInput
        kind='property'
        schema={props.schema}
        value={draft as string | number | object | null | undefined}
        onChange={setDraft}
        disabled={disabled}
        orgId={props.orgId}
        entityProperties={props.entityProperties}
        size='xs'
        // Markdown rows: the bordered rich-text box at the read view's type
        // size, natural height (no 200px form floor), caret at the clicked
        // character.
        markdownDisplay='inline'
        initialCaretPoint={editCaretPoint}
        autoOpenPicker={descriptor.autoOpenPicker}
      />
    );
  } else if (props.kind === 'stage') {
    editor = (
      <CrmFieldValueInput
        kind='stage'
        entityTypeName={props.entityTypeName}
        associationTypeName={props.associationTypeName}
        value={draft as string | null | undefined}
        onChange={handleStageChange}
        disabled={disabled}
        ariaLabel={editLabel}
        size='xs'
        defaultOpen
      />
    );
  } else {
    editor = (
      <CrmFieldValueInput
        kind='association'
        associationType={props.associationType}
        targetEntityTypeName={props.targetEntityTypeName}
        value={draft as string | string[] | null | undefined}
        onChange={setDraft}
        orgId={props.orgId}
        stageTypeNames={props.stageTypeNames}
        sourceEntityId={props.sourceEntityId}
        disabled={disabled}
        ariaLabel={editLabel}
        size='xs'
        // The entry click already expressed "pick a value" — open the picker
        // immediately rather than landing on a closed trigger.
        defaultOpen
      />
    );
  }

  // A floating editor needs its anchor mounted; until the read cell's ref has
  // settled (first paint) the editor opens in the row like every other kind.
  const floating = isEditing && floatsEditor && cellEl !== null;

  let content: ReactNode;
  if (isEditing && !floating) {
    content = (
      <div
        ref={containerRef}
        onBlur={handleBlur}
        onKeyDown={handleKeyDown}
        className={
          isPropertyKind
            ? PROPERTY_EDIT_WRAPPER_CLASS
            : PICKER_EDIT_WRAPPER_CLASS
        }
      >
        {/* The editor keeps its normal form-field chrome (ring, background,
            padding) — the chromeless getChromeOverrides treatment belongs to
            surfaces where the surrounding cell IS the chrome (the table).
            Commit happens on click-outside or Enter (single-line) via
            useInlineEdit — no explicit Save button. */}
        {editor}
        <InlineFieldError message={errorMessage} />
      </div>
    );
  } else if (!isEditable) {
    content = children;
  } else {
    const cellClass = cx(
      CELL_CLASS,
      isPropertyKind ? PROPERTY_CELL_CLASS : PICKER_CELL_CLASS
    );
    content = (
      // Block wrapper so the cell fills the value slot whether the slot is a
      // block (property rows) or a flex-wrap container (badge rows), and so
      // the clear-error pill drops below the cell rather than beside it.
      <div className='min-w-0 flex-1'>
        {/* While a floating editor is open the read cell stays exactly as it
            is — it is the overlay's anchor and what keeps the page's layout
            from moving. The overlay covers it (opaque, 3px overhang), so its
            click/hover affordances are unreachable underneath. Mark it
            aria-hidden + inert so assistive tech and keyboard focus don't
            reach the covered read cell (edit/clear buttons, the click target)
            as a duplicate of the live editor above it. */}
        <div
          ref={setCellRef}
          className={cellClass}
          onClick={handleCellClick}
          aria-busy={clearStatus === 'clearing'}
          aria-hidden={floating ? 'true' : undefined}
          inert={floating || undefined}
        >
          {/* The value, with its trailing control (copy) right beside it.
              A click ON the value's own control keeps its action, a double
              click edits — see useOwnValueClick; the capture handler is
              only armed when there is such a control. */}
          <div
            ref={valueRef}
            onClickCapture={
              hasOwnInteractive ? handleOwnValueClickCapture : undefined
            }
            className='min-w-0'
          >
            {children}
          </div>
          {trailing && (
            <div
              className={cx(
                'flex shrink-0 items-center',
                alignControlsToFirstLine && 'self-start mt-[3px]'
              )}
            >
              {trailing}
            </div>
          )}
          {/* Whitespace: the rest of the cell, so the click target is the
              full column. */}
          <div className='min-w-0 flex-1 self-stretch' aria-hidden='true' />
          <div
            className={cx(
              ACTIONS_CLASS,
              alignControlsToFirstLine && 'self-start mt-[3px]'
            )}
          >
            <RowActionButton
              ref={setTriggerRef}
              icon={Edit02}
              label={editLabel}
              onClick={handleStartEdit}
            />
            {onClear && (
              <RowActionButton
                icon={XClose}
                label={clearLabel}
                onClick={handleClear}
                isDisabled={clearStatus === 'clearing'}
              />
            )}
          </div>
        </div>
        <InlineFieldError message={clearError} />
        {floating && (
          // The popped field: portaled, fixed over the read cell's rect,
          // grows downward OVER the rows below rather than pushing them.
          // card-flush — the editor owns its internal sections (typing
          // area, toolbar) — with no card minimum and the anchor's width, so
          // its text wraps exactly where the read value's does. The wrapper
          // inside is the inline-edit container: useInlineEdit's outside-
          // click / blur / Escape contract and the focus effects above all
          // key off containerRef, which now lives in the portal.
          <CellFieldOverlay
            anchorEl={cellEl}
            mode='editing'
            layout='card-flush'
            cardMinWidthPx={0}
            fitAnchor
            keepParentOverlayOpen
            // Anchored at the cell, never shifted up to fit: the text the
            // user clicked stays under the pointer (and initialCaretPoint
            // keeps resolving); the panel scrolls a tall field into view.
            clampToViewport={false}
            footer={<InlineFieldError message={errorMessage} />}
          >
            <div
              ref={containerRef}
              onBlur={handleBlur}
              onKeyDown={handleKeyDown}
              className='w-full'
            >
              {editor}
            </div>
          </CellFieldOverlay>
        )}
      </div>
    );
  }

  return (
    <OverviewFieldRow
      icon={icon}
      label={label}
      rowClassName={cx('group', rowClassName)}
      valueClassName={valueClassName}
      // On an editable row the trailing control lives in the action cluster.
      trailing={isEditable ? undefined : trailing}
    >
      <div aria-live='assertive' className='sr-only' aria-atomic='true'>
        {announcement}
      </div>
      <div aria-live='assertive' className='sr-only' aria-atomic='true'>
        {clearAnnouncement}
      </div>
      {content}
    </OverviewFieldRow>
  );
}
